import { useMemo } from 'react';
import { PROVINCE_RANKING_FIXTURE, KINGDOM_RANKING_FIXTURE, MOCK_CURRENT_TICK } from './mockRankingsData';
import { RANKING_TOP_N, rankingStatValue } from '../constants/rankings';
import type {
  RankingEntityType,
  RankingEntry,
  RankingScope,
  RankingStatKey,
  RankingsResult,
} from '../types/rankings';

interface UseRankingsResult {
  data: RankingsResult | null;
  loading: boolean;
  error: string | null;
}

/** `location` is always `"kingdom:island"` — the second segment is the island number. */
function islandOf(location: string): number {
  return Number(location.split(':')[1]);
}

/**
 * SF-159 Component 5: mock-data-backed rankings hook — `useChancellery.ts`-shaped (`{data,
 * loading, error}`, synchronous `useMemo`, `loading` always `false`), not the diplomacy hooks'
 * async TTL-cache shape (Phase 3 correction). The future real-API implementation of this one hook
 * is the entire migration (Key Implementation Note 6) — every consumer (`Rankings.tsx`, the two
 * shells, `RankingsTable`) reads only through this `{data, loading, error}` shape.
 *
 * Filters the static fixture by `scope`/`islandNumber`, then re-derives `position` for the
 * requested `sortBy` from the full scoped list (Key Implementation Note 1 — per-column
 * re-ranking, not one frozen `rank` field), sorted `DESC` with a deterministic `id ASC` tie-break,
 * before slicing to `RANKING_TOP_N`. `viewerEntry`'s `position` is read from the full sorted list,
 * never an artifact of the 100-slice.
 */
export function useRankings(
  entityType: RankingEntityType,
  scope: RankingScope,
  sortBy: RankingStatKey,
  islandNumber: number | null
): UseRankingsResult {
  const data = useMemo<RankingsResult>(() => {
    const fixture = entityType === 'province' ? PROVINCE_RANKING_FIXTURE : KINGDOM_RANKING_FIXTURE;
    const scoped =
      scope === 'island' && islandNumber != null
        ? fixture.filter((row) => islandOf(row.location) === islandNumber)
        : fixture;

    const sorted = [...scoped].sort((a, b) => {
      const diff = rankingStatValue(b, sortBy) - rankingStatValue(a, sortBy);
      if (diff !== 0) return diff;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });

    const ranked: RankingEntry[] = sorted.map((row, idx) => ({ ...row, position: idx + 1 }));

    return {
      entries: ranked.slice(0, RANKING_TOP_N),
      totalCount: ranked.length,
      viewerEntry: ranked.find((row) => row.isViewer) ?? null,
      currentTick: MOCK_CURRENT_TICK,
    };
  }, [entityType, scope, sortBy, islandNumber]);

  return { data, loading: false, error: null };
}
